import { useRef, useCallback } from 'react'
import { Flex, Text, InjectedModalProps, useModal } from 'vvs-uikit'
import { useTranslation } from 'contexts/Localization'
import { useSignAndAuthenticate } from 'state/vvsgotchi/hooks/useSignAuthAndAcceptance'
import PixelatedImage from 'views/VvsGotchi/components/PixelatedImage'
import { GotchiModal } from 'views/VvsGotchi/components/Modal'
import ActionButton from 'views/VvsGotchi/components/ActionButton'

export type ModalResult = 'ok' | 'closed'

const AuthModal: React.FC<
  InjectedModalProps & {
    onActionClick: () => Promise<void>
  }
> = ({ onActionClick, onDismiss }) => {
  const { t } = useTranslation()

  return (
    <GotchiModal onDismiss={onDismiss}>
      <Flex justifyContent="center" pb={2} mb={4}>
        <PixelatedImage src="/images/vvsGotchi/vvsgotchi_logo_lg.svg" alt="VvsGotchi Logo" width="224px" />
      </Flex>
      <Text color="black" fontSize="20px" fontWeight="600" mb="8px">
        {t('Authentication Required')}
      </Text>
      <Text color="black" fontWeight="600" my="8px">
        {t('Please sign to verify that you are the owner of this wallet!')}
      </Text>
      <ActionButton mt="16px" onClick={onActionClick}>
        {t('Authenticate')}
      </ActionButton>
    </GotchiModal>
  )
}

export const useAuthModal = () => {
  const signAndAuthenticate = useSignAndAuthenticate()

  const resolveRef = useRef<(result: ModalResult) => void>()

  const modalSwitches = useModal(
    <AuthModal
      onActionClick={async () => {
        await signAndAuthenticate()
        resolveRef.current?.('ok')
      }}
    />,
    false,
    false,
    'vvsgotchiAuth',
    () => resolveRef.current?.('closed'),
  )
  const modalSwitchesRef = useRef(modalSwitches)
  modalSwitchesRef.current = modalSwitches

  return useCallback(() => {
    const [openModal, closeModal] = modalSwitchesRef.current

    if (resolveRef.current) {
      throw new Error('Authentication modal already opened')
    }
    return new Promise<ModalResult>((resolve) => {
      resolveRef.current = resolve
      openModal()
    }).finally(() => {
      resolveRef.current = null
      closeModal()
    })
  }, [])
}
